import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import http from "../../http-common";
import usePageTitle from "../../hooks/usePageTitle";
import { store } from "../../store";

export default function WaitlistTokenLanding({
  pageTitle,
  type, // "status" or "verify"
  loadingTitle,
  loadingDesc,
  successTitle,
  successDesc,
  successToast,
  defaultErrorMsg,
  errorTitle,
  autoRedirect = false,
}) {
  usePageTitle(pageTitle);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [errorMsg, setErrorMsg] = useState("");
  const [fepkId, setFepkId] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg(t("No token provided in the URL."));
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await http.post("/waitlist/verify", { token });
        if (response.data?.token && response.data?.entry) {
          const entryFepkId = response.data.entry.fepk;
          setFepkId(entryFepkId);
          // Read the logged-in id at write time (not as an effect dependency) so a
          // single-use verify token isn't submitted twice while auth rehydrates -
          // matches the scoping in EpkSocialAction/WaitlistJoinModal/WaitlistStatusModal.
          const userId = store.getState().user?.id || '0';
          localStorage.setItem(`waitlist_token_${entryFepkId}_${userId}`, response.data.token);

          if (type === "verify") {
            // Clear any stored referral code for this EPK now that we've joined/verified
            localStorage.removeItem(`waitlist_ref_${entryFepkId}`);
          }

          setStatus("success");
          toast.success(successToast);

          if (autoRedirect) {
            setTimeout(() => {
              navigate(`/epk/${entryFepkId}`);
            }, 1500);
          }
        } else {
          setStatus("error");
          setErrorMsg(t("Verification failed: invalid response from server."));
        }
      } catch (err) {
        setStatus("error");
        setErrorMsg(err.response?.data?.message || defaultErrorMsg);
      }
    };

    verifyToken();
  }, [token, type, successToast, defaultErrorMsg, autoRedirect, navigate, t]);

  const handleAction = () => {
    if (fepkId) {
      navigate(`/epk/${fepkId}`);
    } else {
      navigate("/catalog");
    }
  };

  return (
    <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-[#1E0039] tw-px-4">
      <div className="tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-[#280D41] tw-p-8 tw-text-center tw-shadow-xl tw-border tw-border-white/10">
        {status === "loading" && (
          <div className="tw-flex tw-flex-col tw-items-center">
            <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-[#E81A84] tw-border-t-transparent tw-mb-4" />
            <h2 className="tw-text-xl tw-font-semibold tw-text-white tw-mb-2">
              {loadingTitle}
            </h2>
            <p className="tw-text-sm tw-text-white/60">
              {loadingDesc}
            </p>
          </div>
        )}

        {status === "success" && (
          <div>
            <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-500/20 tw-text-green-500">
              <svg className="tw-h-8 tw-w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-4">
              {successTitle}
            </h2>
            <p className="tw-text-sm tw-text-white/70 tw-mb-6">
              {successDesc}
            </p>
            {!autoRedirect && (
              <button
                onClick={handleAction}
                className="tw-w-full tw-rounded-xl tw-bg-[#FF43A7] tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#e03290] tw-transition-colors tw-border-none tw-cursor-pointer"
              >
                {t("Go to EPK")}
              </button>
            )}
          </div>
        )}

        {status === "error" && (
          <div>
            <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-500/20 tw-text-red-500">
              <svg className="tw-h-8 tw-w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-4">
              {errorTitle}
            </h2>
            <p className="tw-text-sm tw-text-red-400 tw-mb-6">
              {errorMsg}
            </p>
            <button
              onClick={handleAction}
              className="tw-w-full tw-rounded-xl tw-bg-white/10 tw-py-3 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-white/20 tw-transition-colors tw-border-none tw-cursor-pointer"
            >
              {t("Go to Catalog")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
